import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  LogOut,
  RefreshCw,
  Trash2,
  Mail,
  Phone,
  Building2,
  ArrowUpRight,
  Search,
  Download,
  ShieldCheck,
  Save,
  Tag,
  Users,
  Send,
  Calendar,
  Flame,
  Activity,
  Settings as SettingsIcon,
} from "lucide-react";
import { BookingsAdmin, AvailabilityAdmin } from "@/components/admin/BookingsAdmin";
import MessagingAdmin from "@/components/admin/MessagingAdmin";
import PersonalBrandingAdmin from "@/components/admin/PersonalBrandingAdmin";
import FunnelAdmin from "@/components/admin/FunnelAdmin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "bc_admin_token";

const STATUSES = [
  "DNP",
  "Interested",
  "Not Interested",
  "Connect Later",
  "Payment Done",
  "Proposal Sent",
];

const STATUS_STYLES = {
  "DNP": "bg-zinc-800/50 border-zinc-700 text-zinc-400",
  "Interested": "bg-emerald-900/30 border-emerald-700/50 text-emerald-300",
  "Not Interested": "bg-rose-900/25 border-rose-800/40 text-rose-300",
  "Connect Later": "bg-amber-900/25 border-amber-700/40 text-amber-300",
  "Payment Done": "bg-green-700/40 border-green-600/60 text-green-200",
  "Proposal Sent": "bg-blue-900/30 border-blue-700/50 text-blue-300",
};

function statusClass(s) {
  return STATUS_STYLES[s] || "bg-zinc-800/40 border-zinc-700 text-zinc-500";
}

function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

function saveToken(t) {
  localStorage.setItem(TOKEN_KEY, t);
}

function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

function authHeaders() {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// ===== Login =====
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, {
        email: email.trim(),
        password,
      });
      saveToken(data.token);
      toast.success("Signed in");
      onLogin();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-20">
      <div className="w-full max-w-md luxury-card p-8 md:p-10">
        <div className="flex items-center gap-3 mb-7">
          <div className="w-11 h-11 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center">
            <ShieldCheck size={20} className="text-red-500" />
          </div>
          <div>
            <div className="eyebrow">BrandChilly</div>
            <div className="font-serif-display text-2xl">Admin Panel</div>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
          <div>
            <label className="block text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">Email</label>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="brandchilly@gmail.com"
              className="input-luxury"
              data-testid="admin-login-email"
            />
          </div>
          <div>
            <label className="block text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="input-luxury"
              data-testid="admin-login-password"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full justify-center disabled:opacity-60"
            data-testid="admin-login-submit"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <div className="mt-6 text-center text-xs text-zinc-500">
          <Link to="/" className="hover:text-white transition">← Back to BrandChilly</Link>
        </div>
      </div>
    </div>
  );
}

// ===== Dashboard =====
function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("leads");

  const handleLogout = () => {
    clearToken();
    toast.success("Signed out");
    onLogout();
  };

  return (
    <div className="min-h-screen px-4 md:px-8 py-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <div className="eyebrow mb-2">BrandChilly · Admin</div>
            <h1 className="font-serif-display text-4xl md:text-5xl">
              {tab === "leads" && "Inbox"}
              {tab === "bookings" && "Bookings"}
              {tab === "branding" && "Personal Branding"}
              {tab === "messaging" && "Messaging"}
              {tab === "availability" && "Availability"}
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/" className="btn-ghost text-sm py-2.5 px-4">
              <ArrowUpRight size={15} /> Site
            </Link>
            <button onClick={handleLogout} className="btn-primary text-sm py-2.5 px-4" data-testid="admin-logout-btn">
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-7 border-b border-white/5">
          {[
            { id: "leads", label: "Leads", icon: Users },
            { id: "bookings", label: "Bookings", icon: Calendar },
            { id: "funnel", label: "Funnel", icon: Activity },
            { id: "branding", label: "Branding", icon: Flame },
            { id: "messaging", label: "Messaging", icon: Mail },
            { id: "availability", label: "Availability", icon: SettingsIcon },
          ].map(({ id, label, icon: Icon }) => {
            const active = tab === id;
            return (
              <button
                key={id}
                onClick={() => setTab(id)}
                data-testid={`admin-tab-${id}`}
                className={`flex items-center gap-2 px-4 md:px-5 py-3 text-sm transition-colors border-b-2 -mb-px ${
                  active ? "text-white border-red-500" : "text-zinc-500 border-transparent hover:text-white"
                }`}
              >
                <Icon size={15} /> {label}
              </button>
            );
          })}
        </div>

        {tab === "leads" && <LeadsTab onLogout={onLogout} />}
        {tab === "bookings" && <BookingsAdmin onAuthFail={onLogout} />}
        {tab === "funnel" && <FunnelAdmin />}
        {tab === "branding" && <PersonalBrandingAdmin onAuthFail={onLogout} />}
        {tab === "messaging" && <MessagingAdmin />}
        {tab === "availability" && <AvailabilityAdmin onAuthFail={onLogout} />}
      </div>
    </div>
  );
}

// ===== Leads Tab =====
function LeadsTab({ onLogout }) {
  const [leads, setLeads] = useState([]);
  const [campaignBusy, setCampaignBusy] = useState(false);

  const sendCampaign = async (test) => {
    const msg = test
      ? "Send a TEST campaign (email only) to brandchilly@gmail.com?"
      : `Send the audit campaign via EMAIL + WHATSAPP to ALL ${leads.length} leads? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    setCampaignBusy(true);
    try {
      const body = test
        ? { channels: ["email"], test_email: "brandchilly@gmail.com" }
        : { channels: ["email", "whatsapp"] };
      const { data } = await axios.post(`${API}/admin/campaign/send`, body, { headers: authHeaders() });
      toast.success(`Campaign started · ${data.total_leads} recipient(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Campaign failed to start");
    } finally {
      setCampaignBusy(false);
    }
  };
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState("All");
  const [editStatus, setEditStatus] = useState("New");
  const [editRemarks, setEditRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  // Sync editor state when selection changes
  useEffect(() => {
    if (selected) {
      setEditStatus(selected.status || "");
      setEditRemarks(selected.remarks || "");
    }
  }, [selected]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/contact`, { headers: authHeaders() });
      setLeads(data);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        toast.error("Session expired. Please sign in again.");
        clearToken();
        onLogout();
        return;
      }
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    try {
      await axios.delete(`${API}/admin/contact/${id}`, { headers: authHeaders() });
      setLeads((prev) => prev.filter((l) => l.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const saveLead = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/admin/contact/${selected.id}`,
        { status: editStatus, remarks: editRemarks },
        { headers: authHeaders() }
      );
      setLeads((prev) => prev.map((l) => (l.id === data.id ? data : l)));
      setSelected(data);
      toast.success("Lead updated");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not update";
      toast.error(typeof msg === "string" ? msg : "Could not update");
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = () => {
    if (leads.length === 0) {
      toast.error("No leads to export");
      return;
    }
    const headers = ["Date", "Name", "Email", "Phone", "Niche", "Status", "Remarks", "Source", "Message"];
    const rows = leads.map((l) => [
      formatDate(l.created_at),
      l.name,
      l.email,
      l.phone || "",
      l.niche || "",
      l.status || "New",
      (l.remarks || "").replace(/\n/g, " "),
      l.source || "",
      (l.message || "").replace(/\n/g, " "),
    ]);
    const csv = [headers, ...rows]
      .map((r) =>
        r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `brandchilly-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  const filtered = leads.filter((l) => {
    if (statusFilter !== "All") {
      if (statusFilter === "Unset") {
        if (l.status) return false;
      } else if ((l.status || "") !== statusFilter) {
        return false;
      }
    }
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      (l.name || "").toLowerCase().includes(q) ||
      (l.email || "").toLowerCase().includes(q) ||
      (l.phone || "").toLowerCase().includes(q) ||
      (l.niche || "").toLowerCase().includes(q) ||
      (l.message || "").toLowerCase().includes(q) ||
      (l.remarks || "").toLowerCase().includes(q)
    );
  });

  const statusCounts = leads.reduce((acc, l) => {
    const s = l.status || "Unset";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="leads-tab">
      {/* Tools row */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="text-xs c-muted">{leads.length} total · {filtered.length} shown</div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="btn-ghost text-sm py-2 px-3"
            data-testid="admin-refresh-btn"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button onClick={exportCsv} className="btn-ghost text-sm py-2 px-3" data-testid="admin-export-btn">
            <Download size={14} /> CSV
          </button>
          <button
            onClick={() => sendCampaign(true)}
            disabled={campaignBusy}
            className="btn-ghost text-sm py-2 px-3 disabled:opacity-50"
            data-testid="admin-campaign-test-btn"
          >
            <Mail size={14} /> Test Campaign
          </button>
          <button
            onClick={() => sendCampaign(false)}
            disabled={campaignBusy}
            className="btn-primary text-sm py-2 px-3 disabled:opacity-50"
            data-testid="admin-campaign-blast-btn"
          >
            <Send size={14} /> Blast Campaign
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="luxury-card p-3 mb-4 flex items-center gap-3">
        <Search size={16} className="text-zinc-500 ml-2" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, email, niche, message, remarks…"
          className="bg-transparent flex-1 outline-none text-white text-sm py-1"
          data-testid="admin-search-input"
        />
        {query && (
          <button onClick={() => setQuery("")} className="text-xs text-zinc-500 hover:text-white px-2">
            clear
          </button>
        )}
      </div>

      {/* Status filter pills */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <span className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 mr-1 flex items-center gap-1.5">
          <Tag size={11} /> Filter
        </span>
        {["All", "Unset", ...STATUSES].map((s) => {
          const count = s === "All" ? leads.length : statusCounts[s] || 0;
          const active = statusFilter === s;
          return (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                active
                  ? "bg-red-500 border-red-500 text-white"
                  : "bg-zinc-900/50 border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
              }`}
              data-testid={`status-filter-${s.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {s} <span className="opacity-70">·{count}</span>
            </button>
          );
        })}
      </div>

      {/* Empty / loading / table */}
      {loading ? (
        <div className="luxury-card p-16 text-center text-zinc-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="luxury-card p-16 text-center">
          <div className="font-serif-display text-3xl mb-2">No leads {query ? "match this search" : "yet"}.</div>
          <p className="text-zinc-500 font-body">
            {query ? "Try a different search." : "When someone fills the contact form, they'll show up here."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* List */}
          <div className="lg:col-span-7 luxury-card p-2">
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-body">
                <thead className="text-[11px] tracking-[0.2em] uppercase text-zinc-500">
                  <tr className="border-b border-white/5">
                    <th className="text-left px-4 py-3">Date</th>
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3 hidden lg:table-cell">Niche</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l) => (
                    <tr
                      key={l.id}
                      onClick={() => setSelected(l)}
                      className={`border-b border-white/5 cursor-pointer transition-colors ${
                        selected?.id === l.id ? "bg-red-500/5" : "hover:bg-white/[0.02]"
                      }`}
                      data-testid={`lead-row-${l.id}`}
                    >
                      <td className="px-4 py-4 text-zinc-400 whitespace-nowrap">{formatDate(l.created_at)}</td>
                      <td className="px-4 py-4 text-white font-medium">
                        {l.name}
                        <div className="text-[11px] text-zinc-500 mt-0.5 md:hidden">{l.email}</div>
                      </td>
                      <td className="px-4 py-4">
                        {l.status ? (
                          <span
                            className={`inline-flex items-center gap-1.5 text-[10px] tracking-[0.12em] uppercase px-2.5 py-1 rounded-full border ${statusClass(l.status)}`}
                            data-testid={`lead-status-${l.id}`}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                            {l.status}
                          </span>
                        ) : (
                          <span className="text-zinc-600 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-zinc-400 hidden lg:table-cell">{l.niche || "—"}</td>
                      <td className="px-4 py-4 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(l.id);
                          }}
                          className="text-zinc-600 hover:text-red-500 transition p-1"
                          title="Delete"
                          data-testid={`lead-delete-${l.id}`}
                        >
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Detail panel */}
          <div className="lg:col-span-5">
            {selected ? (
              <div className="luxury-card p-7 sticky top-6" data-testid="admin-lead-detail">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="eyebrow mb-1">Lead detail</div>
                    <div className="font-serif-display text-3xl">{selected.name}</div>
                    <div className="text-xs text-zinc-500 mt-1">{formatDate(selected.created_at)}</div>
                  </div>
                  {selected.status ? (
                    <span className={`inline-flex items-center gap-1.5 text-[10px] tracking-[0.12em] uppercase px-2.5 py-1 rounded-full border whitespace-nowrap ${statusClass(selected.status)}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                      {selected.status}
                    </span>
                  ) : (
                    <span className="text-zinc-600 text-xs">No status</span>
                  )}
                </div>

                <div className="space-y-3 mb-6 mt-5">
                  <a
                    href={`mailto:${selected.email}`}
                    className="flex items-center gap-3 text-zinc-300 hover:text-white transition"
                  >
                    <Mail size={15} className="text-red-500" /> {selected.email}
                  </a>
                  {selected.phone && (
                    <a
                      href={`tel:${selected.phone}`}
                      className="flex items-center gap-3 text-zinc-300 hover:text-white transition"
                    >
                      <Phone size={15} className="text-red-500" /> {selected.phone}
                    </a>
                  )}
                  {selected.niche && (
                    <div className="flex items-center gap-3 text-zinc-300">
                      <Building2 size={15} className="text-red-500" /> {selected.niche}
                    </div>
                  )}
                </div>

                {/* STATUS + REMARKS EDITOR */}
                <div className="bg-black/30 border border-white/5 rounded-xl p-4 mb-6">
                  <div className="eyebrow mb-3">Update status & remarks</div>

                  <label className="block text-[11px] tracking-[0.18em] uppercase text-zinc-500 mb-2">Status</label>
                  <select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                    className="input-luxury text-sm py-2.5 mb-4 cursor-pointer"
                    data-testid="lead-status-select"
                  >
                    <option value="" className="bg-black">— Select status —</option>
                    {STATUSES.map((s) => (
                      <option key={s} value={s} className="bg-black">
                        {s}
                      </option>
                    ))}
                  </select>

                  <label className="block text-[11px] tracking-[0.18em] uppercase text-zinc-500 mb-2">Remarks</label>
                  <textarea
                    rows={4}
                    value={editRemarks}
                    onChange={(e) => setEditRemarks(e.target.value)}
                    placeholder="Add internal notes about this lead…"
                    className="input-luxury text-sm resize-none"
                    data-testid="lead-remarks-input"
                  />

                  <button
                    onClick={saveLead}
                    disabled={saving}
                    className="btn-primary w-full justify-center text-sm py-2.5 mt-3 disabled:opacity-60"
                    data-testid="lead-save-btn"
                  >
                    <Save size={14} /> {saving ? "Saving…" : "Save changes"}
                  </button>
                </div>

                <div className="eyebrow mb-3">Message</div>
                <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap font-body text-sm">
                  {selected.message || <span className="text-zinc-600">No message</span>}
                </p>

                <div className="mt-6 flex gap-2">
                  <a
                    href={`mailto:${selected.email}?subject=BrandChilly · Re: your enquiry&body=Hi ${encodeURIComponent(selected.name)},%0A%0A`}
                    className="btn-primary text-sm py-2.5 px-4 flex-1 justify-center"
                  >
                    <Mail size={14} /> Reply
                  </a>
                  <button
                    onClick={() => handleDelete(selected.id)}
                    className="btn-ghost text-sm py-2.5 px-4"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ) : (
              <div className="luxury-card p-10 text-center text-zinc-500 font-body">
                Select a lead to view full details.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Page =====
export default function AdminPage() {
  const [authed, setAuthed] = useState(!!getToken());

  // Verify token on mount
  useEffect(() => {
    if (!getToken()) {
      setAuthed(false);
      return;
    }
    axios
      .get(`${API}/auth/me`, { headers: authHeaders() })
      .then(() => setAuthed(true))
      .catch(() => {
        clearToken();
        setAuthed(false);
      });
  }, []);

  return authed ? (
    <Dashboard onLogout={() => setAuthed(false)} />
  ) : (
    <LoginScreen onLogin={() => setAuthed(true)} />
  );
}
